import * as React from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Grip,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { usePermissions } from '@/lib/use-permissions'
import { useSelectedEntityId } from '@/lib/use-selected-entity'
import { Button } from '@/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { productEntry, productForLeaf, products } from '@/app/module-registry'

import { IsolatedContribution } from './isolated-contribution'

function ProductIcon({ icon: Icon, active }: { icon: LucideIcon; active: boolean }) {
  return (
    <span
      className={cn(
        'mb-2 flex size-7 items-center justify-center rounded-md',
        active
          ? 'bg-primary/12 text-primary'
          : 'bg-muted text-muted-foreground group-hover:text-foreground',
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
    </span>
  )
}

function productClassName(active: boolean) {
  return cn(
    'group flex min-h-24 flex-col items-start rounded-md border p-3 text-left outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30',
    active
      ? 'border-primary/30 bg-primary/[0.07]'
      : 'border-transparent hover:border-border hover:bg-foreground/[0.04]',
  )
}

export function ProductSwitcher() {
  const [open, setOpen] = React.useState(false)
  const activeProduct = useRouterState({ select: (state) => productForLeaf(state.matches.at(-1)?.fullPath) })
  const entityId = useSelectedEntityId()
  const { can } = usePermissions()
  const visibleProducts = products().flatMap((product) => {
    const entry = productEntry(product, can)
    return entry ? [{ product, entry }] : []
  })

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="chrome"
            size="icon-lg"
            aria-label="Open product launcher"
            className="shrink-0"
          />
        }
      >
        <Grip className="size-[18px]" />
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-80 gap-2 p-3">
        <div className="grid grid-cols-2 gap-1.5">
          {visibleProducts.map(({ product, entry }) => {
            const active = product.id === activeProduct?.id
            const to = entityId === null ? product.entityFreeTo : entry.to
            return (
              <IsolatedContribution key={product.label}>
                {to === undefined ? (
                  <button
                    type="button"
                    disabled
                    title="Select an entity first"
                    aria-label={`${product.label} (select an entity first)`}
                    className={cn(productClassName(false), 'cursor-not-allowed opacity-50')}
                  >
                    <ProductIcon icon={product.icon} active={false} />
                    <span className="text-foreground text-xs font-medium">{product.label}</span>
                    <span className="text-muted-foreground mt-0.5 text-[10px]/normal">
                      {product.description}
                    </span>
                  </button>
                ) : (
                  <Link
                    to={to}
                    params={entityId === null ? {} : { entityId }}
                    onClick={() => setOpen(false)}
                    aria-current={active ? 'page' : undefined}
                    className={productClassName(active)}
                  >
                    <ProductIcon icon={product.icon} active={active} />
                    <span className="text-foreground text-xs font-medium">{product.label}</span>
                    <span className="text-muted-foreground mt-0.5 text-[10px]/normal">
                      {product.description}
                    </span>
                  </Link>
                )}
              </IsolatedContribution>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
